import { inject, Injectable } from "@angular/core";
import { combineLatest, Observable, take } from "rxjs";
import {
	SplitWarningModel,
	TaxesConfirmationVariantsModel,
	TaxesPriceVerificationModel,
	TaxesRouteNames,
	TaxesUndeclaredModel,
	TaxesUndeclaredSingleModel,
	TaxesUndeclaredTrxModel,
	TaxesUserStatusModel,
	ToastMessages
} from "@modules/taxes/domain/entities/taxes.model";
import { TaxesUndeclaredTrxStateService } from "@modules/taxes/domain/+state/taxes-undeclared-trx-state.service";
import { TaxesStateService } from "@modules/taxes/domain/+state/taxes.state.service";
import { TaxesNavigationService } from "@modules/taxes/domain/infrastructure/taxes-navigation.service";
import { ToastrService } from "ngx-toastr";
import { TaxesRemote } from "@modules/taxes/domain/infrastructure/taxes.remote";
import { TranslationService } from "@core-services/translation.service";
import { TaxesTrackerFacade } from "@modules/taxes/domain/+state/taxes-tracker-facade.service";
import { TAXES_SUCCESS_TOAST } from "@modules/taxes/domain/entities/taxes-tracking.model";
import { TaxesQueryParamName, TaxesQueryParamsCollection } from "@modules/taxes/domain/entities/taxes-navigation.model";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { CryptoTransaction } from "@models/transaction";
import { TransactionWrapperModalComponent } from "@modules/transaction/modals/transaction-wrapper-modal/transaction-wrapper-modal.component";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { ModalService } from "@core-services/modal.service";

export interface UndeclaredSingleTrxVM {
	undeclaredSingleTrx: TaxesUndeclaredTrxModel[];
	undeclaredSingleTrxData: TaxesUndeclaredModel;
	isValidationLoading: boolean;
	isCoinSwapped: boolean;
	higherThanFirstBoundary: boolean;
	higherThanSecondBoundary: boolean;
	isSplitFlow: boolean;
	sliceNumber: number;
	amountLeft: number;
	isSplitDepositActiveFF: boolean;
}
interface UndeclaredTrxVM {
	undeclaredMultiTrx: TaxesUndeclaredTrxModel[];
	undeclaredSingleTrxVM: UndeclaredSingleTrxVM;
	apiLoading: boolean;
	splitWarning: SplitWarningModel[];
}

@Injectable()
export class TaxesUndeclaredTrxFacade {
	private readonly undeclaredTrxState = inject(TaxesUndeclaredTrxStateService);
	private readonly taxesTrackerFacade = inject(TaxesTrackerFacade);
	private readonly cryptoWalletService = inject(CryptoWalletService);
	private readonly navigationService = inject(TaxesNavigationService);
	private readonly launchDarklyService = inject(LaunchDarklyService);
	private readonly translationService = inject(TranslationService);
	private readonly taxesStateService = inject(TaxesStateService);
	private readonly modalService = inject(ModalService);
	private readonly taxesRemote = inject(TaxesRemote);
	private readonly toastr = inject(ToastrService);

	public readonly isSplitDepositActive$ = this.launchDarklyService.isActive$(LD_FLAG.TAX_SPLIT_DEPOSIT);
	public readonly undeclaredTrxData$: Observable<TaxesUndeclaredSingleModel> = this.undeclaredTrxState.trxSingleData$;

	public readonly undeclaredSingleTrxVM$: Observable<UndeclaredSingleTrxVM> = combineLatest({
		undeclaredSingleTrx: this.undeclaredTrxState.trxSingle$,
		undeclaredSingleTrxData: this.undeclaredTrxState.trxSingleData$,
		isValidationLoading: this.undeclaredTrxState.isValidationLoading$,
		isCoinSwapped: this.undeclaredTrxState.isCoinSwapped$,
		higherThanFirstBoundary: this.undeclaredTrxState.higherThanFirstBoundary$,
		higherThanSecondBoundary: this.undeclaredTrxState.higherThanSecondBoundary$,
		sliceNumber: this.undeclaredTrxState.sliceNumber$,
		amountLeft: this.undeclaredTrxState.amountLeft$,
		isSplitFlow: this.undeclaredTrxState.isSplitDeposit$,
		isSplitDepositActiveFF: this.isSplitDepositActive$
	});

	public readonly undeclaredTrxVM$: Observable<UndeclaredTrxVM> = combineLatest({
		undeclaredMultiTrx: this.undeclaredTrxState.trxMulti$,
		undeclaredSingleTrxVM: this.undeclaredSingleTrxVM$,
		apiLoading: this.undeclaredTrxState.isLoading$,
		splitWarning: this.undeclaredTrxState.splitWarning$
	});

	public setMultiUndeclaredTrxState(undeclaredTrx: TaxesUndeclaredTrxModel[]): void {
		this.undeclaredTrxState.updateTrxMulti(undeclaredTrx);
	}

	public setSingleUndeclaredTrxState(undeclaredTrx: TaxesUndeclaredTrxModel): void {
		this.undeclaredTrxState.updateTrxSingle(undeclaredTrx);
	}

	public setSingleUndeclaredTrxDataState(
		undeclaredTrx: TaxesUndeclaredTrxModel,
		undeclaredTrxData: TaxesUndeclaredSingleModel
	): void {
		this.undeclaredTrxState.updateTrxSingleData(undeclaredTrx, undeclaredTrxData);
	}

	// Non Split Flow
	public fetchNonSplitPriceValidation(
		priceValidation: TaxesPriceVerificationModel,
		undeclaredTrx: TaxesUndeclaredTrxModel,
		isDateRange: boolean
	): void {
		this.undeclaredTrxState.setIsValidationLoading(true);

		this.taxesRemote.getPriceVerification$(priceValidation).subscribe({
			next: (response) => {
				this.undeclaredTrxState.updateStateNonSplit(response, undeclaredTrx);
				this.nonSplitNavigation(response.higherThanFirstBoundary, isDateRange);
			},
			error: () => {
				this.undeclaredTrxState.setIsValidationLoading(false);
				// Show toast error
				this.toastr.error(this.translationService.translate(ToastMessages.Error));
			}
		});
	}

	private nonSplitNavigation(isHigherFirstBoundary: boolean, isDateRange: boolean): void {
		const path: TaxesRouteNames = isHigherFirstBoundary
			? TaxesRouteNames.SwapQuestion
			: TaxesRouteNames.Confirmation;

		const variants: TaxesConfirmationVariantsModel = isDateRange
			? TaxesConfirmationVariantsModel.DateRangeSelected
			: TaxesConfirmationVariantsModel.DateOnlySelected;

		this.navigationService
			.routeParamsReader()
			.pipe(take(1))
			.subscribe({
				next: async (query) => {
					if (query.fromTradeFlow) {
						this.navigationService.updateQueryParamsAndNavigate(path, {
							...query,
							NavigationFrom: TaxesRouteNames.SingleBuyDate,
							NavigationVariant: variants
						});
					} else {
						// Navigate to the next modal and write the QP
						this.navigationService.updateQueryParamsAndNavigate(path, {
							NavigationFrom: TaxesRouteNames.SingleBuyDate,
							NavigationVariant: variants
						});
					}
				}
			});
	}

	public setSwapAndNavigateNonSplit(
		isCoinSwapped: boolean,
		undeclaredTrx: TaxesUndeclaredTrxModel,
		qp: TaxesQueryParamName
	): void {
		this.undeclaredTrxState.setCoinSwappedNonSplit(isCoinSwapped, undeclaredTrx);

		this.navigationService
			.routeParamsReader()
			.pipe(take(1))
			.subscribe({
				next: async (query) => {
					if (query.fromTradeFlow) {
						// Navigate to the next modal and write the QP
						this.navigationService.updateQueryParamsAndNavigate(TaxesRouteNames.Confirmation, {
							...query,
							NavigationFrom: TaxesRouteNames.SwapQuestion,
							NavigationVariant: qp
						});
					} else {
						// Navigate to the next modal and write the QP
						// Navigate to the next modal and write the QP
						this.navigationService.updateQueryParamsAndNavigate(TaxesRouteNames.Confirmation, {
							NavigationFrom: TaxesRouteNames.SwapQuestion,
							NavigationVariant: qp
						});
					}
				}
			});
	}

	// Split Flow
	public splitFlow(priceValidation: TaxesPriceVerificationModel, undeclaredTrx: TaxesUndeclaredTrxModel): void {
		this.undeclaredTrxState.updateSplitFlowState();

		if (undeclaredTrx.price_or_date_unknown === null && undeclaredTrx.bought_before_cutoff_date === null) {
			this.fetchSplitPriceValidation(priceValidation, undeclaredTrx, +undeclaredTrx.amount);
		} else {
			this.undeclaredTrxState.updateStateSplitOnPriceOrCutOff(
				this.removeNullProperties(undeclaredTrx),
				+undeclaredTrx.amount
			);
			this.splitNavigation();
		}
	}

	private fetchSplitPriceValidation(
		priceValidation: TaxesPriceVerificationModel,
		undeclaredTrx: TaxesUndeclaredTrxModel,
		declaredAmount: number
	): void {
		this.taxesRemote.getPriceVerification$(priceValidation).subscribe({
			next: (response) => {
				this.undeclaredTrxState.updateStateSplit(
					response,
					this.removeNullProperties(undeclaredTrx),
					declaredAmount
				);
				this.splitNavigation(response.higherThanFirstBoundary);
			},
			error: () => {
				this.undeclaredTrxState.setIsValidationLoading(false);
				// Show toast error
				this.toastr.error(this.translationService.translate(ToastMessages.Error));
			}
		});
	}

	private splitNavigation(isHigherFirstBoundary?: boolean): void {
		let path: TaxesRouteNames;
		let sliceNumber: number;

		this.undeclaredTrxState.amountLeft$.pipe(take(1)).subscribe((amountLeft) => {
			this.undeclaredTrxState.setIsValidationLoading(false);

			if (isHigherFirstBoundary === undefined) {
				if (amountLeft === 0) {
					path = TaxesRouteNames.Confirmation;
				} else {
					sliceNumber = this.undeclaredTrxState.setSliceNumber();
					path = TaxesRouteNames.SingleBuyDate;
				}
			} else {
				if (isHigherFirstBoundary) {
					path = TaxesRouteNames.SwapQuestion;
					this.undeclaredTrxState.sliceNumber$.pipe(take(1)).subscribe((slice) => (sliceNumber = slice));
				} else if (amountLeft === 0) {
					path = TaxesRouteNames.Confirmation;
				} else {
					sliceNumber = this.undeclaredTrxState.setSliceNumber();
					path = TaxesRouteNames.SingleBuyDate;
				}
			}
		});
		this.updateSplitNavigation(path, sliceNumber);
	}

	public setSwapAndNavigateSplit(isCoinSwapped: boolean): void {
		let path: TaxesRouteNames;
		let sliceNumber: number;
		this.undeclaredTrxState.setCoinSwappedSplit(isCoinSwapped);

		this.undeclaredTrxState.amountLeft$.pipe(take(1)).subscribe((amountLeft) => {
			if (amountLeft > 0) {
				sliceNumber = this.undeclaredTrxState.setSliceNumber();
				path = TaxesRouteNames.SingleBuyDate;
			} else {
				path = TaxesRouteNames.Confirmation;
			}
		});
		this.navigationService
			.routeParamsReader()
			.pipe(take(1))
			.subscribe({
				next: async (query) => {
					if (query.fromTradeFlow) {
						this.updateSplitNavigation(path, sliceNumber, { ...query });
					} else {
						this.updateSplitNavigation(path, sliceNumber);
					}
				}
			});
		this.updateSplitNavigation(path, sliceNumber);
	}

	private updateSplitNavigation(
		path: TaxesRouteNames,
		sliceNumber: number,
		query?: Partial<TaxesQueryParamsCollection>
	): void {
		this.navigationService.updateQueryParamsAndNavigate(path, {
			...query,
			NavigationFrom: TaxesRouteNames.SingleBuyDate,
			NavigationVariant: null,
			SliceNumber: sliceNumber
		});
	}

	private removeNullProperties(undeclaredTrx: TaxesUndeclaredTrxModel): TaxesUndeclaredTrxModel {
		const newUndeclaredTrx: TaxesUndeclaredTrxModel = {
			transaction_id: undeclaredTrx.transaction_id,
			amount: undeclaredTrx.amount
		};

		for (const key in undeclaredTrx) {
			if (undeclaredTrx.hasOwnProperty(key) && undeclaredTrx[key] !== null) {
				if (typeof undeclaredTrx[key] === "object" && !Array.isArray(undeclaredTrx[key])) {
					newUndeclaredTrx[key] = this.removeNullProperties(undeclaredTrx[key]);
				} else {
					newUndeclaredTrx[key] = undeclaredTrx[key];
				}
			}
		}
		return newUndeclaredTrx;
	}

	// Fetching the trx to the API and navigating the user to the correct page
	public fetchUndeclaredTrx(
		fiatPid: string,
		undeclaredTrx: TaxesUndeclaredTrxModel[],
		isFromTrxDetails: "true" | "false",
		trxData: TaxesUndeclaredSingleModel
	): void {
		this.undeclaredTrxState.setIsLoading(true);

		this.taxesRemote.updateUndeclaredTransactions$(fiatPid, undeclaredTrx).subscribe({
			next: (response) => {
				this.successUpdatingUndeclaredTrx(response, isFromTrxDetails, trxData);
			},
			error: () => {
				this.errorUpdatingUndeclaredTrx();
			}
		});
	}

	private successUpdatingUndeclaredTrx(
		apiResponse: TaxesUserStatusModel,
		isFromTrxDetails: "true" | "false",
		trxData: TaxesUndeclaredSingleModel
	): void {
		if (isFromTrxDetails === "true") {
			this.openTrxDetailModal(trxData.transactionId, trxData.walletId);
		} else {
			// Navigate to next page when user don't come from trx details
			this.handleNavigation(apiResponse.openDeclarationsCount);
		}

		// Update states
		this.undeclaredTrxState.resetState();
		this.taxesStateService.updateState({
			declarationsOpen: apiResponse.openDeclarationsCount
		});

		// Rudderstack Tracker
		this.taxesTrackerFacade.tracker(TAXES_SUCCESS_TOAST);
	}

	private errorUpdatingUndeclaredTrx(): void {
		// Update state
		this.undeclaredTrxState.setIsLoading(false);

		// Show toast and Navigate to next page
		this.toastr.error(this.translationService.translate(ToastMessages.Error));
	}

	private handleNavigation(declarationsCount: number): void {
		this.navigationService
			.routeParamsReader()
			.pipe(take(1))
			.subscribe({
				next: async (query) => {
					const _query = query.fromTradeFlow ? query : null;
					if (declarationsCount > 0) {
						this.navigationService.gotoByNavigationUrl(TaxesRouteNames.CryptoDeclaration, _query);
						// Show Success toast
						this.toastr.success(this.translationService.translate(ToastMessages.Success));
					} else {
						this.navigationService.gotoByNavigationUrl(TaxesRouteNames.Success, _query);
					}
				}
			});
	}

	public cleanTrxState(): void {
		this.undeclaredTrxState.resetState();
	}

	public goToCryptoDeclaration(isSplitFlow?: boolean, navigateTo?: string): void {
		const path: TaxesRouteNames = isSplitFlow ? TaxesRouteNames.CancelModal : TaxesRouteNames.CryptoDeclaration;

		this.navigationService
			.routeParamsReader()
			.pipe(take(1))
			.subscribe({
				next: async (query) => {
					if (query.fromTradeFlow) {
						this.navigationService.updateQueryParamsAndNavigate(path, {
							...query,
							NavigateTo: navigateTo ? navigateTo : null
						});
					} else {
						this.navigationService.updateQueryParamsAndNavigate(path, {
							NavigateTo: navigateTo ? navigateTo : null
						});
					}
				}
			});
	}

	public goToCancelOrClose(navigateTo?: string): void {
		this.navigationService
			.routeParamsReader()
			.pipe(take(1))
			.subscribe((query) => {
				if (query.fromTradeFlow) {
					this.navigationService.updateQueryParamsAndNavigate(TaxesRouteNames.CancelModal, {
						...query,
						NavigateTo: navigateTo ? navigateTo : null
					});
				} else {
					this.navigationService.updateQueryParamsAndNavigate(TaxesRouteNames.CancelModal, {
						NavigateTo: navigateTo ? navigateTo : null
					});
				}
			});
	}

	public openTrxDetailModal(trxId: string, walletId: string): void {
		// State have to be clean when leaving the flow to avoid possible conflicts
		this.cleanTrxState();
		this.navigationService.closeModal();
		this.cryptoWalletService
			.getTransaction(walletId, trxId)
			.pipe(take(1))
			.subscribe((ctx: CryptoTransaction) => {
				this.modalService
					.create(TransactionWrapperModalComponent, {
						transaction: ctx,
						canOpen: true,
						activity: "activity"
					})
					.present();
			});
	}

	public onLeaveOrBackToTrxDetails(isFromTrxDetails: "true" | "false", trxId: string, walletId: string): void {
		if (isFromTrxDetails === "true") {
			this.openTrxDetailModal(trxId, walletId);
		} else {
			this.goToCryptoDeclaration();
		}
	}
}
